import React, { useEffect } from 'react';

import { IconButton, Box } from '@mui/material';
import { ReactFlow, Background, Controls, useNodesState, useEdgesState } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import styles from './FlowChartCanvas.module.css';

import { ReactComponent as ExpandIcon } from 'asset/IconsPack/at_expand-01-filled.svg';
import { ReactComponent as MinimizeIcon } from 'asset/IconsPack/at_minimize-01-filled.svg';

// Edge types
import LogicalEdge from './components/edges/LogicalEdge';
import YesNoEdge from './components/edges/YesNoEdge';
import CheckNode from './components/nodes/CheckNode';
import DecisionNode from './components/nodes/DecisionNode';
import GroupNode from './components/nodes/GroupNode';
// Node types
import HeaderNode from './components/nodes/HeaderNode';
import IdentityMethodNode from './components/nodes/IdentityMethodNode';
import TerminalNode from './components/nodes/TerminalNode';
import { prepareCustomFieldFlowChartData } from './helper/utils';

const NODE_TYPES = {
    header: HeaderNode,
    group: GroupNode,
    identityMethod: IdentityMethodNode,
    check: CheckNode,
    decision: DecisionNode,
    terminal: TerminalNode,
};

const EDGE_TYPES = {
    logical: LogicalEdge,
    yesno: YesNoEdge,
};

function FlowChartCanvas({
    workflowConfig,
    openPreview,
    handleOpenPreview,
    previewControllerEnabled = true,
}) {
    const canvasWrapperStyle =
        openPreview || !previewControllerEnabled ? styles.fullWrapper : styles.fixedWrapper;

    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);

    useEffect(() => {
        if (!workflowConfig) return;

        const [preparedNodes, preparedEdges] = prepareCustomFieldFlowChartData(workflowConfig);

        setNodes(preparedNodes);
        setEdges(preparedEdges);
    }, [workflowConfig, setNodes, setEdges]);

    return (
        <Box className={styles.flowchartContainer}>
            {previewControllerEnabled && (
                <IconButton
                    className={clsx(styles.controlIconsWrapper, styles.maxScreenControl)}
                    onClick={handleOpenPreview}
                >
                    {openPreview ? (
                        <MinimizeIcon className={styles.maxControlIcon} />
                    ) : (
                        <ExpandIcon className={styles.maxControlIcon} />
                    )}
                </IconButton>
            )}
            <Box className={canvasWrapperStyle}>
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    nodeTypes={NODE_TYPES}
                    edgeTypes={EDGE_TYPES}
                    nodesDraggable={false}
                    nodesConnectable={false}
                    elementsSelectable={false}
                    fitView
                    fitViewOptions={{ padding: 0.15 }}
                    minZoom={0.1}
                    maxZoom={2}
                >
                    <Background color="#E5E7EB" gap={20} />
                    <Controls showInteractive={false} position="bottom-right" />
                </ReactFlow>
            </Box>
        </Box>
    );
}

FlowChartCanvas.propTypes = {
    workflowConfig: PropTypes.object,
    openPreview: PropTypes.bool,
    handleOpenPreview: PropTypes.func,
    previewControllerEnabled: PropTypes.bool,
};

export default FlowChartCanvas;
